import type { ClientError } from '@anchorage/sentry';
import {
  WrappedError,
  handleError,
  isHtmlResponseError,
} from '@anchorage/sentry';
import type {
  DefaultOptions,
  TypePolicies,
  WatchQueryFetchPolicy,
} from '@apollo/client';
import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
} from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { ErrorLink } from '@apollo/client/link/error';
import {
  KEEP,
  removeTypenameFromVariables,
} from '@apollo/client/link/remove-typename';
import { RetryLink } from '@apollo/client/link/retry';
import type { GraphQLFormattedError } from 'graphql';

import fragmentMatcher from '../../generated/fragment-matcher';
import { isUnauthenticatedError } from '../apollo-errors';
import { markSchemaMismatch } from './schemaMismatch';

// A GRAPHQL_VALIDATION_FAILED failure ships as an HTTP 400, which Apollo v4's
// HttpLink treats as a transport-layer ServerError (this `result.errors`
// shape, not CombinedGraphQLErrors) even though the body is valid GraphQL —
// see packages/sentry's gqlValidationFingerprint for the same shape check.
type ServerErrorLike = {
  result?: {
    errors?: { extensions?: { code?: unknown } }[];
  };
};

const hasSchemaValidationFailure = (error: Error): boolean => {
  const errors = (error as ServerErrorLike).result?.errors;
  return (
    Array.isArray(errors) &&
    errors.some(
      (gqlError) => gqlError?.extensions?.code === 'GRAPHQL_VALIDATION_FAILED',
    )
  );
};

export type ApolloClientConfig = {
  /** Base URI for GraphQL endpoint. Default: '/graphql' */
  uri?: string;
  /** Additional headers to include in requests */
  headers?: Record<string, string>;
  /** Custom cache type policies */
  typePolicies?: TypePolicies;
  /** Enable Apollo DevTools. Default: true in development */
  devTools?: boolean;
  /** Additional Apollo links to insert before the terminating link */
  additionalLinks?: ApolloLink[];
  /** Custom network error handler that fully replaces the default.
   * When provided, `onUnauthenticated` has no effect (handle 401s yourself).
   * If not provided, uses a default that ignores server disconnect statuses
   * (499, 502, 503, 504) and "Failed to fetch" errors, and reports all others to Sentry.
   * `operationName` identifies the failed request — a network error carries no
   * GraphQL body, so it is the only thing that does. */
  onNetworkError?: (error: Error, operationName?: string) => void;
  /** Called on a 401. Receives the raw response when the error carries one:
   * an expired session and a policy denial are both 401, so only headers
   * separate them. */
  onUnauthenticated?: (response?: Response) => void;
  /** Default watchQuery fetch policy. Default: 'network-only' */
  fetchPolicy?: WatchQueryFetchPolicy;
  /** Identifies this app to Apollo Server/Studio and Datadog APM via the
   * `apollographql-client-name` header (Apollo Client's `clientAwareness`
   * feature). One per FE app, e.g. 'frontoffice', 'backoffice'. */
  name?: string;
  /** Paired with `name` — sent as `apollographql-client-version`. Not the
   * Apollo Client library version. */
  version?: string;
};

const createNetworkErrorHandler =
  (onUnauthenticated?: (response?: Response) => void) =>
  (error: Error, operationName?: string) => {
    // Forwarded, not interpreted: the app branches on headers so this
    // package never learns any app's routes.
    if (isUnauthenticatedError(error) && onUnauthenticated) {
      onUnauthenticated((error as { response?: Response }).response);
      return;
    }

    if ('statusCode' in error) {
      const status = (error as Error & { statusCode: number }).statusCode;

      // Ignore server disconnect statuses — expected during deploys/restarts
      const serverDisconnectStatuses = [499, 502, 503, 504];
      if (serverDisconnectStatuses.includes(status)) {
        return;
      }
    }

    // Ignore network connectivity issues — user's internet is down or request blocked
    if (error.toString().includes('Failed to fetch')) {
      return;
    }

    handleError(error, operationName ? { operationName } : undefined);
  };

// Shared error link — handles all GraphQL + network errors via v4 ErrorLink.
// The handler receives a single `error` value (vs v3's destructured
// `{ graphQLErrors, networkError }`) and discriminates using
// `CombinedGraphQLErrors.is(...)`. Toast/skipToast/factory contract is
// preserved from the v3 implementation.
const createErrorLink = (
  onNetworkError: (error: Error, operationName?: string) => void,
): ErrorLink => {
  return new ErrorLink(({ error, operation }) => {
    if (CombinedGraphQLErrors.is(error)) {
      const ctx = operation.getContext();

      error.errors.forEach((graphqlError: GraphQLFormattedError) => {
        // GRAPHQL_VALIDATION_FAILED can only happen against a schema that no
        // longer matches this bundle's queries — it's proof the tab is stale,
        // not an ordinary server error. Reported to Sentry below as usual;
        // this just additionally lets a stale-tab banner offer a reload. The
        // far more common case — a non-2xx response body — never reaches
        // here; see hasSchemaValidationFailure below for that path.
        if (graphqlError.extensions?.code === 'GRAPHQL_VALIDATION_FAILED') {
          markSchemaMismatch();
        }

        // Auto-toast is the default for GraphQL errors. Components can:
        //   - `context: { skipToast: true }` — suppress the toast entirely.
        //   - `context: { skipErrorReport: true }` — suppress the Sentry
        //     report for this operation's GraphQL errors. For probes whose
        //     errors are expected and non-actionable (e.g. a permission check
        //     the BE answers with an error). Orthogonal to the toast — pair
        //     with `skipToast` to fully silence the operation.
        //   - `context: { toast: { title?, description?, variant?, action? } }`
        //     — static overrides. The `description` field is treated as a
        //     **fallback**: the server's `error.message` wins when present,
        //     and the static description is shown only when the server
        //     message is empty. This matches the pre-migration
        //     `description: error.message || 'fallback'` idiom that was used
        //     across the codebase. Other fields (title/variant/action) are
        //     hard overrides.
        //   - `context: { toast: (error) => ({...}) }` — dynamic factory when
        //     the toast contents depend on the error at runtime (callers can
        //     return any description, and the factory return value wins
        //     verbatim — no server-message merge).
        const isFactory = typeof ctx.toast === 'function';
        const resolvedToast = isFactory
          ? ctx.toast(graphqlError as ClientError)
          : ctx.toast;
        const toastConfig = ctx.skipToast
          ? undefined
          : {
              ...(resolvedToast ?? {}),
              // Description policy: factory return wins verbatim. For static
              // overrides, server `error.message` takes precedence; the
              // static value acts as a fallback when the server message is
              // empty. This preserves the old `error.message || 'fallback'`
              // semantics across migrated call sites without per-callsite
              // edits.
              description: isFactory
                ? resolvedToast?.description
                : graphqlError.message || resolvedToast?.description,
            };

        // `context.errorWrapperMessage` lets callers prepend a
        // human-readable label to the Sentry report (replacing the
        // pre-migration `handleError(new WrappedError('label', e))` pattern
        // inside an onError). Toast still uses the unwrapped error message.
        const errorForReport = ctx.errorWrapperMessage
          ? new WrappedError(
              ctx.errorWrapperMessage as string,
              graphqlError as unknown as Error,
            )
          : (graphqlError as unknown as ClientError);

        handleError(errorForReport, {
          operationName: operation.operationName,
          operationVariables: operation.variables,
          ...(toastConfig ? { toastConfig } : {}),
          ...(ctx.skipErrorReport ? { skipErrorReport: true } : {}),
        });
      });
      return;
    }

    // Non-GraphQL → network/protocol-layer error.
    if (error instanceof Error) {
      if (hasSchemaValidationFailure(error)) {
        markSchemaMismatch();
      }

      // An HTML response body means a load balancer error page or similar, not
      // a GraphQL result. Wrap the original error in a non-destructive proxy so
      // the user-facing `.message` is friendlier without mutating the live
      // error or losing the structured fields (`name`, `statusCode`,
      // `bodyText`, `response`) that downstream handlers
      // (`createNetworkErrorHandler`, `handleError`'s HTML suppression) depend
      // on. Detection lives in `@anchorage/sentry` because `handleError` keys
      // its suppression off the same signal.
      let errorToReport: Error = error;
      if (isHtmlResponseError(error)) {
        errorToReport = Object.assign(
          Object.create(Object.getPrototypeOf(error) as object),
          error,
          { message: 'Network Error: Please try again' },
        );
        // `Object.assign` copies own *enumerable* properties, and `stack` is
        // non-enumerable — without this the proxy carries no stack at all
        // (`Object.create` doesn't generate one), so anything reporting it
        // groups on the message alone with no frames.
        Object.defineProperty(errorToReport, 'stack', {
          value: error.stack,
          configurable: true,
          writable: true,
        });
      }
      onNetworkError(errorToReport, operation.operationName);
    }
  });
};

// Shared retry link — retries on network failures
const createRetryLink = () =>
  new RetryLink({
    delay: {
      initial: 3000,
      max: Infinity,
      jitter: true,
    },
    attempts: {
      max: 15,
      retryIf: (error) => {
        return error && error.toString().includes('Failed to fetch');
      },
    },
  });

// URL editing link — appends query name for debugging. Call sites sharing one
// document (e.g. a generic `Operations` query) are indistinguishable in the
// network tab; pass `context: { queryNameOverride: 'SomeLabel' }` to disambiguate.
// v4: `operation`/`forward` come from the ApolloLink request signature directly;
// no `NextLink` import needed.
const createUrlEditingLink = (uri: string) =>
  new ApolloLink((operation, forward) => {
    const queryNameOverride = operation.getContext().queryNameOverride as
      | string
      | undefined;
    const operationName =
      queryNameOverride || operation.operationName || 'UnnamedOperation';
    const separator = uri.includes('?') ? '&' : '?';

    operation.setContext(() => ({
      uri: `${uri}${separator}queryName=${encodeURIComponent(operationName)}`,
    }));

    return forward(operation);
  });

// Strips `__typename` from outgoing variables so query results can be reused
// as mutation inputs without tripping the gateway's strict (Router 2.x) input
// validation (CEXP-21004).
//
// `except` keys match a variable's TOP-LEVEL declared type; nested freeform
// fields use the `{ TypeName: { field: KEEP } }` form. We except the only
// freeform-JSON *input* in the schema — `PrepareActionFromJSONFormInput.formData`
// (`JSON!`) — whose value may legitimately contain a `__typename` key that must
// not be stripped. (The `JSON`/`Map` scalars are otherwise output-only, so no
// other variable carries freeform JSON today.)
//
// MAINTENANCE: if a new operation passes a `JSON`/`Map` (or serialized-JSON
// `String`) value as a variable, add a field-level `except` entry for its input
// type here BEFORE enabling the strip for it — otherwise the key is silently
// removed.
const createRemoveTypenameLink = () =>
  removeTypenameFromVariables({
    except: {
      PrepareActionFromJSONFormInput: {
        formData: KEEP,
      },
    },
  });

const createCache = (typePolicies?: TypePolicies) =>
  new InMemoryCache({
    possibleTypes: fragmentMatcher.possibleTypes,
    typePolicies,
  });

export function createApolloClient(
  config: ApolloClientConfig = {},
): ApolloClient {
  const {
    typePolicies,
    devTools = process.env.NODE_ENV !== 'production',
    additionalLinks = [],
    onNetworkError,
    onUnauthenticated,
    fetchPolicy = 'network-only',
  } = config;

  const defaultOptions: DefaultOptions = {
    watchQuery: {
      fetchPolicy,
      // Left at v4's default: a global false would also suppress loading:true
      // on useLazyQuery's first exec. Poll suppression belongs in
      // usePollingQuery/usePaginatedQuery, which derive it per query.
    },
  };

  const networkErrorHandler =
    onNetworkError ?? createNetworkErrorHandler(onUnauthenticated);

  const uri = config.uri ?? '/graphql';

  const httpLink = new HttpLink({
    uri,
    credentials: 'same-origin',
    headers: config.headers,
  });

  // Build link chain: retry → error → urlEditing → removeTypename → [additional] → http
  // removeTypename strips `__typename` from all outgoing variables — query
  // results carry it and input types reject it (CEXP-21004). It must run before
  // the terminating link so the stripped variables are what goes over the wire.
  const links: ApolloLink[] = [
    createRetryLink(),
    createErrorLink(networkErrorHandler),
    createUrlEditingLink(uri),
    createRemoveTypenameLink(),
    ...additionalLinks,
    httpLink,
  ];

  return new ApolloClient({
    link: ApolloLink.from(links),
    cache: createCache(typePolicies),
    devtools: { enabled: devTools },
    defaultOptions,
    ...(config.name && {
      clientAwareness: { name: config.name, version: config.version },
    }),
  });
}
